/**
 * Workspace layout descriptor + resolver.
 *
 * The CLI lays out config-as-code under a single, product-namespaced root in the
 * user's git repo:
 *
 *   wayai-ws/
 *     org/                 # org-as-code (shared resources)
 *     hubs/<hub>/          # one folder per preview hub
 *     bases/<base>/        # one folder per preview base
 *
 * This replaces the legacy split layout (`workspace/<hub>/` for hubs + a
 * root-level `org/`). `resolveLayout` reads EITHER layout so existing repos —
 * and their CI that runs `wayai push` against a committed `workspace/` — keep
 * working until `wayai migrate` relocates them. New/fresh repos get the new
 * layout. We never read both at once (no split-brain): the new layout wins.
 *
 * `bases/` has no legacy counterpart — the subtree postdates the legacy layout,
 * so `basesDir` is always the new-layout path even in an un-migrated repo (see
 * `resolveLayout`).
 */

import * as fs from 'node:fs';
import * as path from 'node:path';
import { WAYAI_WORKSPACE_LAYOUT } from '@wayai/core';

export interface WorkspaceLayout {
  /** Product-namespaced root dir at the git root, e.g. 'wayai-ws'. */
  wsDir: string;
  /** Subdir of `wsDir` holding hub folders, e.g. 'hubs'. */
  hubsSubdir: string;
  /** Subdir of `wsDir` holding org-as-code, e.g. 'org'. */
  orgSubdir: string;
  /** Subdir of `wsDir` holding base folders, e.g. 'bases'. */
  basesSubdir: string;
  /** Pre-migration locations to fall back to on read. */
  legacy: { wsDir: string; orgAtRoot: string };
}

/**
 * The active CLI layout: the canonical dir NAMES from `@wayai/core`
 * (`WAYAI_WORKSPACE_LAYOUT`, shared with the backend `download_workspace`
 * endpoint) plus the CLI-only `legacy` fallback locations. The CLI bundles
 * `@wayai/core` at build time (tsup `noExternal`), so this is a direct import —
 * no hand-copied literal, no drift-guard needed.
 *
 * One descriptor, three subtrees (`hubs/`, `org/`, `bases/`) — not one
 * descriptor per product (docs/reference/rekor-platform-contracts.md §3.3).
 */
export const WAYAI_LAYOUT: WorkspaceLayout = {
  ...WAYAI_WORKSPACE_LAYOUT,
  legacy: { wsDir: 'workspace', orgAtRoot: 'org' },
};

export interface ResolvedLayout {
  /** Absolute dir whose direct children are hub folders. */
  hubsDir: string;
  /** Absolute dir for org-as-code (`resources.yaml` + `resources/`). */
  orgDir: string;
  /**
   * Absolute dir whose direct children are base folders. Always the new-layout
   * path: the subtree postdates the legacy layout, so an un-migrated repo has
   * no legacy location a base folder could have been written to.
   */
  basesDir: string;
  /** True when reading the pre-migration layout (`workspace/` + root `org/`). */
  isLegacy: boolean;
  /** True when the new layout is active but stale legacy dirs ALSO exist. */
  legacyAlsoPresent: boolean;
}

/** True when `p` exists and is a directory (swallows ENOENT/EACCES). */
export function isDirectory(p: string): boolean {
  try {
    return fs.statSync(p).isDirectory();
  } catch {
    return false;
  }
}

/**
 * Resolve the active hub + org directories for a repo, handling the legacy
 * layout. Pure (no side effects) so it's trivially testable — call
 * `warnLayoutOnce` separately for the user-facing nudge.
 */
export function resolveLayout(
  gitRoot: string,
  layout: WorkspaceLayout = WAYAI_LAYOUT,
): ResolvedLayout {
  const newWs = path.join(gitRoot, layout.wsDir);
  const legacyWs = path.join(gitRoot, layout.legacy.wsDir);
  const legacyOrg = path.join(gitRoot, layout.legacy.orgAtRoot);

  const newExists = isDirectory(newWs);
  const legacyExists = isDirectory(legacyWs) || isDirectory(legacyOrg);
  // Never legacy-mapped: see `ResolvedLayout.basesDir`.
  const basesDir = path.join(newWs, layout.basesSubdir);

  if (newExists) {
    return {
      hubsDir: path.join(newWs, layout.hubsSubdir),
      orgDir: path.join(newWs, layout.orgSubdir),
      basesDir,
      isLegacy: false,
      legacyAlsoPresent: legacyExists,
    };
  }

  if (legacyExists) {
    return {
      hubsDir: legacyWs,
      orgDir: legacyOrg,
      basesDir,
      isLegacy: true,
      legacyAlsoPresent: false,
    };
  }

  // Fresh repo → new layout (materialized on demand by init/pull/org).
  return {
    hubsDir: path.join(newWs, layout.hubsSubdir),
    orgDir: path.join(newWs, layout.orgSubdir),
    basesDir,
    isLegacy: false,
    legacyAlsoPresent: false,
  };
}

/** Convenience: the dir whose direct children are hub folders. */
export function resolveHubsDir(gitRoot: string): string {
  return resolveLayout(gitRoot).hubsDir;
}

/** Convenience: the org-as-code dir. */
export function resolveOrgDir(gitRoot: string): string {
  return resolveLayout(gitRoot).orgDir;
}

/** Convenience: the dir whose direct children are base folders. */
export function resolveBasesDir(gitRoot: string): string {
  return resolveLayout(gitRoot).basesDir;
}

/**
 * Display label for the hubs dir relative to the git root (e.g. "wayai-ws/hubs",
 * or "workspace" for a legacy repo) — for user-facing messages. Falls back to the
 * canonical new-layout label (derived from WAYAI_LAYOUT, never a bare literal)
 * when not in a git repo.
 */
export function hubsDirLabel(gitRoot: string | null): string {
  if (!gitRoot) return path.join(WAYAI_LAYOUT.wsDir, WAYAI_LAYOUT.hubsSubdir);
  return path.relative(gitRoot, resolveLayout(gitRoot).hubsDir);
}

/**
 * Display label for the bases dir relative to the git root. Constant in
 * practice ("wayai-ws/bases") because the subtree has no legacy mapping, but
 * derived through the resolver so it tracks a future layout change the way
 * `hubsDirLabel` does.
 */
export function basesDirLabel(gitRoot: string | null): string {
  if (!gitRoot) return path.join(WAYAI_LAYOUT.wsDir, WAYAI_LAYOUT.basesSubdir);
  return path.relative(gitRoot, resolveLayout(gitRoot).basesDir);
}

let _layoutWarned = false;

/** Reset the one-time warning guard (test-only). */
export function _resetLayoutWarning(): void {
  _layoutWarned = false;
}

/**
 * Emit a user-facing nudge about the layout, at most once per process for the
 * interactive case. When stale legacy dirs sit alongside the new layout, warn
 * LOUDLY on every run in non-TTY/CI — a CI `wayai push` that silently ignores
 * edits to a committed `workspace/` is a footgun. Callers must commit
 * `wayai migrate` in the same change that updates their CI.
 */
export function warnLayoutOnce(gitRoot: string): void {
  const r = resolveLayout(gitRoot);

  if (r.legacyAlsoPresent) {
    const msg =
      `Warning: both ${WAYAI_LAYOUT.wsDir}/ and legacy ${WAYAI_LAYOUT.legacy.wsDir}/ ` +
      `(or root ${WAYAI_LAYOUT.legacy.orgAtRoot}/) exist. The legacy dirs are IGNORED. ` +
      `Finish \`wayai migrate\` and delete them.`;
    // Non-TTY (CI): always loud, never suppressed.
    if (!process.stdout.isTTY) {
      console.warn(msg);
      return;
    }
    if (!_layoutWarned) {
      _layoutWarned = true;
      console.warn(msg);
    }
    return;
  }

  if (r.isLegacy && !_layoutWarned) {
    _layoutWarned = true;
    console.warn(
      `Note: using the legacy ${WAYAI_LAYOUT.legacy.wsDir}/ layout. ` +
        `Run \`wayai migrate\` to move to ${WAYAI_LAYOUT.wsDir}/.`,
    );
  }
}
